import React, { useEffect, useState, useContext, useCallback } from "react";
import { LoadingIcon } from "../../../components";
import { gatherExcludedDatesForRow, getPercentile80_95Json } from "./utils";
import { UserContext } from "../../../context/UserContext";
import dayjs from "dayjs";

import ExcludedDatesTable from "./ExcludedDatesTable";
import { BACKEND_DATE_FORMAT } from "../../../consts";
import { AveragePercentileCard } from "./AveragePercentileCard";
import { Forecast } from "./Forecast";
import { Graphs } from "./Graphs";

const LeafSizeContent = ({
  currentOrg,
  currentSite,
  graphData,
  allRowGraphData,
  dropdownLoading,
  dateList,
  siteMetadata,
  currentCrop,
  currentGreenhouse,
  currentPlot,
  tableData,
  allRowTableData,
  greenHouseLabel,
  blockLabel,
}) => {
  const BIN_START = 0;
  const BIN_END = 15;
  const KEY_IN_DATA = "leaf_lengths_cm";
  const GRAPH_UI_DATE_FORMAT = "DD MMM YY";
  const { userData } = useContext(UserContext);

  const [minSize, setMinSize] = useState(BIN_START);
  const [maxSize, setMaxSize] = useState(BIN_END);

  // Distribution / average lines
  const [filteredGraphData, setFilteredGraphData] = useState([]);
  const [averageData, setAverageData] = useState([]);
  const [allRowAverageData, setAllRowAverageData] = useState([]);
  const [ndviAverageData, setNdviAverageData] = useState([]);
  const [allRowNdviAverageData, setAllRowNdviAverageData] = useState([]);
  const [showPercentage, setShowPercentage] = useState(false);
  // Forecast
  const [forecastLeafSizeLength, setForecastLeafSizeLength] = useState([]);
  const [isAllRowCombined, setIsAllRowCombined] = useState(false);

  const [excludedLeafSizeData, setExcludedLeafSizeData] = useState([]);
  const [filteredAllRowData, setFilteredAllRowData] = useState({});

  const [filterRules, setFilterRules] = useState([]);

  useEffect(() => {
    if (!siteMetadata) setFilterRules([]);
    setFilterRules(siteMetadata?.FORECAST_GENERIC_FILTER_RULES || []);
  }, [siteMetadata]);

  useEffect(() => {
    if (!allRowGraphData || Object.keys(allRowGraphData).length === 0) {
      return;
    }
    if (
      !filterRules?.length ||
      !allRowTableData ||
      Object.keys(allRowTableData).length === 0
    ) {
      setFilteredAllRowData(mergeRowAndDateWiseData(allRowGraphData));
      return;
    }
    (async function filterAllRowGraph() {
      const filtered = {};
      const dateKeys = Object.keys(allRowGraphData).sort();
      for (let date of dateKeys) {
        const rowArr = allRowGraphData[date];
        const keep = [];
        for (let rowObj of rowArr) {
          const rowName = rowObj.rowName;
          const plantingDate = allRowTableData?.[rowName]?.plantingDate || null;
          const harvestDate = allRowTableData?.[rowName]?.harvestDate || null;

          let percentile80_95 =
            rowObj?.percentiles?.["80_95"]?.average_length_cm;
          const lengths = rowObj[KEY_IN_DATA]?.map((item) => item[1]) || [];

          if (!percentile80_95) {
            percentile80_95 = await getPercentile80_95Json(
              date,
              lengths,
              currentOrg,
              currentSite.id,
              currentSite.name,
              currentCrop.name,
              currentGreenhouse.name,
              rowObj.rowName,
              siteMetadata
            );
          }

          const singleDateAverageItem = [
            {
              date: dayjs(date, BACKEND_DATE_FORMAT).format(
                GRAPH_UI_DATE_FORMAT,
              ),
              averageLength: rowObj.average_length_cm || 0,
              averagePercentile80_95: percentile80_95,
            },
          ];
          const exDates = gatherExcludedDatesForRow(
            filterRules,
            singleDateAverageItem,
            plantingDate,
            harvestDate,
          );
          if (!exDates?.length) {
            keep.push({
              ...rowObj,
              averagePercentile80_95: percentile80_95,
            });
          }
        }
        if (keep.length) filtered[date] = keep;
      }
      setFilteredAllRowData(mergeRowAndDateWiseData(filtered));
    })();
  }, [allRowGraphData, allRowTableData, filterRules]);

  const mergeRowAndDateWiseData = useCallback((data) => {
    const mergedData = {};
    if (!data) return mergedData;
    Object.entries(data).forEach(([date, rowValue]) => {
      if (!mergedData[date]) {
        mergedData[date] = { leaf_lengths_cm: [] };
      }
      let lengths = [];
      rowValue.forEach((item) => {
        const leafLengths = item.leaf_lengths_cm || [];
        lengths.push(...leafLengths);
      });
      mergedData[date].leaf_lengths_cm = lengths;
    });
    return mergedData;
  }, []);

  if (dropdownLoading) {
    return <LoadingIcon height="200px" />;
  }

  return (
    <div className="flex flex-col items-center">
      <div className="flex lg:flex-col flex-row w-full mb-4">
        <div className="headsize-section w-full flex flex-col items-center lg:justify-center justify-start px-10 sm:px-4 py-4 lg:mr-0 mr-4 lg:mb-4 rounded-md shadow-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-100">
          {/* Percentage Calculator Section */}
          <AveragePercentileCard
            currentOrg={currentOrg}
            currentSite={currentSite}
            currentCrop={currentCrop}
            currentGreenhouse={currentGreenhouse}
            currentPlot={currentPlot}
            dateList={dateList}
            siteMetadata={siteMetadata}
          />
        </div>
        <div className="headsize-section w-full flex flex-col items-center justify-center px-10 py-4 sm:px-4 rounded-md shadow-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-100">
          {/* Spinach Forecast Card */}
          <Forecast
            currentSite={currentSite}
            siteMetadata={siteMetadata}
            currentGreenhouse={currentGreenhouse}
            averageData={isAllRowCombined ? allRowAverageData : averageData}
            tableData={tableData}
            setForecastLeafSizeLength={setForecastLeafSizeLength}
          />
        </div>
      </div>

      {userData?.is_superuser && excludedLeafSizeData.length > 0 && (
        <div className="flex lg:flex-col flex-row w-full mb-4">
          <div className="headsize-section w-full flex flex-col items-center lg:justify-center justify-start rounded-md shadow-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-100">
            <ExcludedDatesTable excludedData={excludedLeafSizeData} />
          </div>
        </div>
      )}

      {/* Graph Section */}
      <Graphs
        currentOrg={currentOrg}
        currentSite={currentSite}
        currentCrop={currentCrop}
        currentGreenhouse={currentGreenhouse}
        currentPlot={currentPlot}
        siteMetadata={siteMetadata}
        BIN_END={BIN_END}
        graphData={graphData}
        allRowGraphData={filteredAllRowData}
        dateList={dateList}
        minSize={minSize}
        maxSize={maxSize}
        showPercentage={showPercentage}
        setShowPercentage={setShowPercentage}
        filteredGraphData={filteredGraphData}
        setFilteredGraphData={setFilteredGraphData}
        setAverageData={setAverageData}
        setNdviAverageData={setNdviAverageData}
        setAllRowAverageData={setAllRowAverageData}
        setAllRowNdviAverageData={setAllRowNdviAverageData}
        averageData={averageData}
        ndviAverageData={ndviAverageData}
        allRowAverageData={allRowAverageData}
        allRowNdviAverageData={allRowNdviAverageData}
        forecastLeafSizeLength={forecastLeafSizeLength}
        isAllRowCombined={isAllRowCombined}
        setIsAllRowCombined={setIsAllRowCombined}
        tableData={tableData}
        filterRules={filterRules}
        setExcludedLeafSizeData={setExcludedLeafSizeData}
        greenHouseLabel={greenHouseLabel}
        blockLabel={blockLabel}
      />
    </div>
  );
};

export default LeafSizeContent;
